import React, { useState, useEffect } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, CircularProgress, Typography, Paper
} from '@mui/material';
import {
  CloudUpload, CloudDownload, Restore, Schedule,
  Delete, Info, Warning, CheckCircle, Error, Close
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/nl';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_CONFIG } from '../../config/api';

const BackupDialog = ({ open, onClose, environment }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  
  // Set moment locale
  moment.locale('nl');
  
  // Create axios instance for central backend
  const centralApi = axios.create({
    baseURL: API_CONFIG.centralBackendURL,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${Cookies.get('auth_token')}`
    },
    timeout: 30000
  });

  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState({ open: false, targetEnvironment: '' });
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [environments, setEnvironments] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [currentSchedule, setCurrentSchedule] = useState(null);
  
  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    environment_id: environment?.id || '',
    schedule_type: 'daily',
    schedule_time: '02:00',
    retention_days: 30,
    is_active: true
  });

  useEffect(() => {
    if (open && environment) {
      loadBackups();
      loadEnvironments();
      loadSchedule();
    }
    
    // Cleanup polling on close
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    };
  }, [open, environment]);

  const loadBackups = async () => {
    if (!environment) return;
    
    setLoading(true);
    try {
      // Laad backups van centrale backend
      const backupsResponse = await centralApi.get(`/api/v1/backup/environments/${environment.id}/backups`);
      setBackups(backupsResponse.data || []);
    } catch (error) {
      console.error('Error loading backups:', error);
      setBackups([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSchedule = async () => {
    if (!environment) return;
    
    try {
      // Laad backup schedule van centrale backend
      const scheduleResponse = await centralApi.get(`/api/v1/backup/environments/${environment.id}/schedule`);
      if (scheduleResponse.data) {
        setCurrentSchedule(scheduleResponse.data);
        setScheduleForm({
          ...scheduleForm,
          ...scheduleResponse.data
        });
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
    }
  };

  const loadEnvironments = async () => {
    try {
              const envResponse = await centralApi.get('/api/v1/environments');
      setEnvironments(envResponse.data.environments || []);
    } catch (error) {
      console.error('Error loading environments:', error);
    }
  };

  const pollBackupStatus = async (jobId) => {
    try {
      // Poll status via centrale backend (die intern de RevPi pollt)
      const response = await centralApi.get(`/api/v1/backup/environments/${environment.id}/backup/${jobId}/status`);
      const status = response.data;
      
      if (status.status === 'completed') {
        // Stop polling
        setPollingInterval(prevInterval => {
          if (prevInterval) {
            clearInterval(prevInterval);
          }
          return null;
        });
        
        setActiveJob(null);
        enqueueSnackbar('Backup succesvol afgerond!', { variant: 'success' });
        loadBackups(); // Herlaad backup lijst
      } else if (status.status === 'failed') {
        // Stop polling bij fout
        setPollingInterval(prevInterval => {
          if (prevInterval) {
            clearInterval(prevInterval);
          }
          return null;
        });
        
        setActiveJob(null);
        enqueueSnackbar(`Backup mislukt: ${status.error || 'Onbekende fout'}`, { variant: 'error' });
      } else {
        // Update status
        setActiveJob(status);
      }
    } catch (error) {
      console.error('Error polling backup status:', error);
      // Stop polling bij herhaalde fouten
      setPollingInterval(prevInterval => {
        if (prevInterval) {
          clearInterval(prevInterval);
        }
        return null;
      });
      setActiveJob(null);
    }
  };

  const handleCreateBackup = async () => {
    try {
      // Start backup via centrale backend
      const response = await centralApi.post(`/api/v1/backup/environments/${environment.id}/backup`);
      
      const { job_id, status } = response.data;
      
      enqueueSnackbar('Backup gestart...', { variant: 'info' });
      setActiveJob({ job_id, status });
      
      // Start polling voor status updates
      const interval = setInterval(() => {
        pollBackupStatus(job_id);
      }, 2000); // Poll elke 2 seconden
      
      setPollingInterval(interval);
      
    } catch (error) {
      console.error('Error creating backup:', error);
      
      // Specifieke error handling
      if (error.response?.status === 503) {
        enqueueSnackbar(error.response.data.detail || 'Backup service tijdelijk niet beschikbaar', { 
          variant: 'warning',
          autoHideDuration: 6000 
        });
      } else if (error.response?.status === 500) {
        enqueueSnackbar('Server fout bij aanmaken backup. Probeer het later opnieuw.', { variant: 'error' });
      } else {
        enqueueSnackbar('Fout bij starten backup', { variant: 'error' });
      }
    }
  };

  const handleDownloadBackup = async (backupId) => {
    try {
      // Download via centrale backend
      const response = await centralApi.get(`/api/v1/backup/backups/${backupId}/download`, {
        responseType: 'blob'
      });
      
      // Download bestand
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup_${backupId}.sql.gz`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      enqueueSnackbar('Backup gedownload', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Fout bij downloaden backup', { variant: 'error' });
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup || !restoreDialog.targetEnvironment) return;
    
    try {
      await centralApi.post(`/api/v1/backup/backups/${selectedBackup.id}/restore`, {
        target_environment_id: restoreDialog.targetEnvironment
      });
      enqueueSnackbar('Restore proces gestart', { variant: 'success' });
      setRestoreDialog({ open: false, targetEnvironment: '' });
    } catch (error) {
      enqueueSnackbar('Fout bij restore', { variant: 'error' });
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Weet je zeker dat je deze backup wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.')) {
      return;
    }
    
    try {
      // Delete via centrale backend
      await centralApi.delete(`/api/v1/backup/backups/${backupId}`);
      
      enqueueSnackbar('Backup succesvol verwijderd!', { variant: 'success' });
      loadBackups(); // Herlaad backup lijst
      
    } catch (error) {
      console.error('Error deleting backup:', error);
      enqueueSnackbar('Fout bij verwijderen backup', { variant: 'error' });
    }
  };

  const handleSaveSchedule = async () => {
    try {
      // Converteer schedule_time naar HH:MM formaat indien nodig
      let formattedScheduleForm = { ...scheduleForm };
      
      // Als schedule_time een nummer is (seconden sinds middernacht), converteer naar HH:MM
      if (typeof scheduleForm.schedule_time === 'number') {
        const hours = Math.floor(scheduleForm.schedule_time / 3600);
        const minutes = Math.floor((scheduleForm.schedule_time % 3600) / 60);
        formattedScheduleForm.schedule_time = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
      
      // Sla schedule op in centrale backend
      if (currentSchedule) {
        // Update bestaande schedule
        await centralApi.put(`/api/v1/backup/schedules/${currentSchedule.id}`, formattedScheduleForm);
      } else {
        // Maak nieuwe schedule
        await centralApi.post('/api/v1/backup/schedules', formattedScheduleForm);
      }
      enqueueSnackbar('Backup schema opgeslagen', { variant: 'success' });
      setScheduleDialog(false);
      loadSchedule(); // Herlaad schedule
    } catch (error) {
      enqueueSnackbar('Fout bij opslaan schema', { variant: 'error' });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'failed':
        return <Error color="error" />;
      case 'running':
        return <CircularProgress size={20} />;
      case 'pending':
        return <Info color="primary" />;
      case 'corrupted':
        return <Warning color="warning" />;
      default:
        return <Info />;
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!environment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Backup Beheer - {environment.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Action Buttons */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={handleCreateBackup}
            disabled={activeJob !== null}
          >
            {activeJob ? 'Backup bezig...' : 'Nieuwe Backup'}
          </Button>
          
          {activeJob && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Status: {activeJob.status}
              </Typography>
            </Box>
          )}
          
          <Button
            variant="outlined"
            startIcon={<Schedule />}
            onClick={() => {
              setScheduleForm({ ...scheduleForm, environment_id: environment.id });
              setScheduleDialog(true);
            }}
          >
            {currentSchedule ? 'Schema Wijzigen' : 'Schema Instellen'}
          </Button>
          
          {currentSchedule && (
            <Chip 
              label={`${currentSchedule.schedule_type} om ${currentSchedule.schedule_time}`}
              size="small"
              color={currentSchedule.is_active ? "success" : "default"}
            />
          )}
        </Box>

        {/* Backups Table */}
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Grootte</TableCell>
                <TableCell>Aangemaakt</TableCell>
                <TableCell>Checksum</TableCell>
                <TableCell>Restored</TableCell>
                <TableCell align="right">Acties</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : backups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Geen backups gevonden
                  </TableCell>
                </TableRow>
              ) : (
                backups.map((backup) => (
                  <TableRow key={backup.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon(backup.status)}
                        <Chip 
                          label={backup.status} 
                          size="small"
                          color={backup.status === 'completed' ? 'success' : 'default'}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>{backup.backup_type}</TableCell>
                    <TableCell>{formatBytes(backup.backup_size)}</TableCell>
                    <TableCell>
                      {moment(backup.created_at).format('DD MMM YYYY HH:mm')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {backup.checksum ? backup.checksum.substring(0, 8) + '...' : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {backup.restored_count || 0} keer
                      {backup.last_restored_at && (
                        <Typography variant="caption" display="block">
                          Laatst: {moment(backup.last_restored_at).format('DD-MM-YYYY')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadBackup(backup.id)}
                        disabled={backup.status !== 'completed'}
                        title="Download backup"
                      >
                        <CloudDownload />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedBackup(backup);
                          setRestoreDialog({ open: true, targetEnvironment: '' });
                        }}
                        disabled={backup.status !== 'completed'}
                        title="Restore backup"
                      >
                        <Restore />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteBackup(backup.id)}
                        title="Verwijder backup"
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialog} onClose={() => setScheduleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Backup Schema Instellen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Schema Type</InputLabel>
              <Select
                value={scheduleForm.schedule_type}
                onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_type: e.target.value })}
                label="Schema Type"
              >
                <MenuItem value="hourly">Elk uur</MenuItem>
                <MenuItem value="daily">Dagelijks</MenuItem>
                <MenuItem value="weekly">Wekelijks</MenuItem>
                <MenuItem value="monthly">Maandelijks</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label="Tijd"
              type="time"
              value={scheduleForm.schedule_time}
              onChange={(e) => setScheduleForm({ ...scheduleForm, schedule_time: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            
            <TextField
              label="Retentie (dagen)"
              type="number"
              value={scheduleForm.retention_days}
              onChange={(e) => setScheduleForm({ ...scheduleForm, retention_days: parseInt(e.target.value) })}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Actief</InputLabel>
              <Select
                value={scheduleForm.is_active}
                onChange={(e) => setScheduleForm({ ...scheduleForm, is_active: e.target.value })}
                label="Actief"
              >
                <MenuItem value={true}>Ja</MenuItem>
                <MenuItem value={false}>Nee</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialog(false)}>Annuleren</Button>
          <Button onClick={handleSaveSchedule} variant="contained">Opslaan</Button>
        </DialogActions>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog 
        open={restoreDialog.open} 
        onClose={() => setRestoreDialog({ open: false, targetEnvironment: '' })} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Backup Restore</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Let op: Restore zal alle huidige data in de target environment overschrijven!
          </Alert>
          
          <FormControl fullWidth>
            <InputLabel>Target Environment</InputLabel>
            <Select
              value={restoreDialog.targetEnvironment}
              onChange={(e) => setRestoreDialog({ ...restoreDialog, targetEnvironment: e.target.value })}
              label="Target Environment"
            >
              {environments.map((env) => (
                <MenuItem key={env.id} value={env.id}>
                  {env.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, targetEnvironment: '' })}>
            Annuleren
          </Button>
          <Button 
            onClick={handleRestoreBackup} 
            variant="contained" 
            color="warning"
            disabled={!restoreDialog.targetEnvironment}
          >
            Restore Starten
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default BackupDialog; 